import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Button,
  LinearProgress,
  Divider,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Print as PrintIcon,
  ArrowBack as ArrowBackIcon,
  Assessment as AssessmentIcon,
  Timeline as TimelineIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  PhotoLibrary as PhotoLibraryIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  CameraAlt as CameraAltIcon,
  Visibility as VisibilityIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { PatientReportData, getPatientReportData, generatePatientReportPDF } from '../../services/patientReportApi';
import { Snackbar } from '@mui/material';

const PatientReport: React.FC = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();

  const [reportData, setReportData] = useState<PatientReportData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [generatingPDF, setGeneratingPDF] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);

  useEffect(() => {
    if (patientId) {
      loadReportData();
    }
  }, [patientId]);

  const loadReportData = async () => {
    if (!patientId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log(`Loading comprehensive report for patient: ${patientId}`);
      const data = await getPatientReportData(patientId);
      setReportData(data);
    } catch (error: any) {
      console.error('Failed to load patient report:', error);
      setError('Failed to load patient report data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleGeneratePDF = async () => {
    if (!patientId) return;
    
    setGeneratingPDF(true);
    setError(null);
    
    try {
      console.log('Starting PDF generation process...');
      
      // First test if the backend is available
      try {
        const testResponse = await fetch(`${process.env.REACT_APP_API_URL}/reports/test`);
        const testData = await testResponse.json();
        console.log('Backend test response:', testData);
        
        if (!testData.reportlab_available) {
          throw new Error('PDF generation library not available on server');
        }
      } catch (testError) {
        console.error('Backend test failed:', testError);
        throw new Error('Cannot connect to PDF generation service');
      }
      
      console.log('Generating PDF report via backend...');
      
      const pdfBlob = await generatePatientReportPDF(patientId);
      
      if (!pdfBlob || pdfBlob.size === 0) {
        throw new Error('Received empty PDF file');
      }
      
      console.log('PDF generated successfully, size:', pdfBlob.size);
      
      // Create download link
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `mole_mapping_report_${patientId}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setSnackbarMessage('PDF report generated successfully!');
      setSnackbarOpen(true);
      
    } catch (error: any) {
      console.error('Failed to generate PDF:', error);
      
      let errorMessage = 'Failed to generate PDF report.';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data) {
        errorMessage = error.response.data.detail || 'Server error occurred';
      }
      
      setError(errorMessage);
      setSnackbarMessage('PDF generation failed');
      setSnackbarOpen(true);
    } finally {
      setGeneratingPDF(false);
    }
  };

  const getRiskColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'high': return theme.palette.error.main;
      case 'moderate': return theme.palette.warning.main;
      case 'low': return theme.palette.success.main;
      default: return theme.palette.grey[500];
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'worsening': return <TrendingUpIcon color="error" />;
      case 'improving': return <TrendingDownIcon color="success" />;
      case 'stable': return <TrendingFlatIcon color="action" />;
      default: return <TrendingFlatIcon color="action" />;
    }
  };

  // Helper function to simplify mole labels
  const simplifyMoleLabel = (originalLabel: string, index: number): string => {
    // Extract number from original label if it exists
    const numbers = originalLabel.match(/\d+/);
    if (numbers) {
      return `Mole ${numbers[0]}`;
    }
    // If no number, use sequential numbering
    return `Mole ${index + 1}`;
  };

  if (loading) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <LinearProgress sx={{ mb: 2, maxWidth: 300, mx: 'auto' }} />
            <Typography variant="h6" gutterBottom>
              Generating Patient Report
            </Typography>
            <Typography color="text.secondary">
              Compiling data from all sessions and assessments...
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (error || !reportData) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Failed to load report data'}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <>
      {/* Enhanced Print Styles */}
  

      <Box id="patient-report-container" sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
        {/* Print Controls - Enhanced with PDF generation */}
        <Box className="no-print" sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
          >
            Go back
          </Button>
        
          <Box sx={{ display: 'flex', gap: 1 }}>
          
            <Button
              variant="contained"
              startIcon={generatingPDF ? undefined : <PrintIcon />}
              onClick={handleGeneratePDF}
              disabled={generatingPDF}
            >
              {generatingPDF ? 'Generating PDF...' : 'Download PDF'}
            </Button>
          </Box>
        </Box>

        {/* Report Header with Logo */}
        <Paper elevation={2} className="report-header print-keep-together" sx={{ p: 4, mb: 3, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={2}>
              {/* Noetiv Logo */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
               
                <Box sx={{ ml: 2, display: { xs: 'none', md: 'block' } }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', lineHeight: 1 }}> 
                    NOETIV
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                    MoleExpert
                  </Typography> *
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={7}>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
                Mole Mapping Report
              </Typography>
              <Typography variant="h5" color="text.secondary" gutterBottom>
                {reportData.patientName}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Patient ID: {reportData.patientId}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Report Generated: {new Date(reportData.reportDate).toLocaleDateString()}
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={3} sx={{ textAlign: 'right' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Chip 
                  label={`${reportData.summary.totalSessions} Total Sessions`} 
                  color="primary" 
                  variant="filled"
                />
                <Chip 
                  label={`${reportData.summary.totalMoles} Monitored Lesions`} 
                  color="info" 
                  variant="filled"
                />
                {reportData.summary.highRiskMoles > 0 && (
                  <Chip 
                    label={`${reportData.summary.highRiskMoles} High Risk`} 
                    color="error" 
                    variant="filled"
                    icon={<WarningIcon />}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Executive Summary */}
        <Card sx={{ mb: 3 }} className="print-section">
          <CardContent>
            <Box className="heading-group">
              <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AssessmentIcon color="primary" />
                Executive Summary
              </Typography>
            </Box>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom color="primary">
                  Session Overview
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2">
                    <strong>Total Sessions:</strong> {reportData.summary.totalSessions}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Baseline Sessions:</strong> {reportData.sessions.filter(s => s.sessionType === 'baseline').length}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Follow-up Sessions:</strong> {reportData.summary.followupSessions}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Last Session:</strong> {reportData.summary.lastSessionDate ? new Date(reportData.summary.lastSessionDate).toLocaleDateString() : 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom color="primary">
                  Assessment Overview
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2">
                    <strong>Total Lesions:</strong> {reportData.summary.totalMoles}
                  </Typography>
                  <Typography variant="body2">
                    <strong>ABCDE Assessments:</strong> {reportData.summary.totalABCDEAssessments}
                  </Typography>
                  <Typography variant="body2" sx={{ color: reportData.summary.highRiskMoles > 0 ? 'error.main' : 'text.primary' }}>
                    <strong>High Risk Lesions:</strong> {reportData.summary.highRiskMoles}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Trending Lesions:</strong> {reportData.riskTrends.filter(t => t.trend !== 'stable').length}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Recommendations */}
        {reportData.recommendations.length > 0 && (
          <Card sx={{ mb: 3 }} className="print-section">
            <CardContent>
              <Box className="heading-group">
                <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningIcon color="warning" />
                  Clinical Recommendations
                </Typography>
              </Box>
              
              {reportData.recommendations.map((recommendation, index) => (
                <Alert 
                  key={index} 
                  severity={recommendation.includes('high-risk') || recommendation.includes('Immediate') ? 'error' : 'warning'}
                  className="recommendation-item"
                  sx={{ mb: 1 }}
                >
                  {recommendation}
                </Alert>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Risk Trends Summary */}
        {reportData.riskTrends.length > 0 && (
          <Card sx={{ mb: 3 }} className="print-section">
            <CardContent>
              <Box className="heading-group">
                <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TimelineIcon color="primary" />
                  Risk Trend Analysis
                </Typography>
              </Box>
              
              <TableContainer className="risk-trends-table">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Lesion</strong></TableCell>
                      <TableCell><strong>Assessments</strong></TableCell>
                      <TableCell><strong>Current Risk</strong></TableCell>
                      <TableCell><strong>Trend</strong></TableCell>
                      <TableCell><strong>Score Range</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reportData.riskTrends.map((trend, index) => {
                      const latestAssessment = trend.assessments[trend.assessments.length - 1];
                      const earliestAssessment = trend.assessments[0];
                      const simplifiedLabel = simplifyMoleLabel(trend.label, index);
                      
                      return (
                        <TableRow key={trend.annotationId}>
                          <TableCell>{simplifiedLabel}</TableCell>
                          <TableCell>{trend.assessments.length}</TableCell>
                          <TableCell>
                            <Chip 
                              label={latestAssessment?.riskLevel || 'Unknown'} 
                              color={latestAssessment?.riskLevel === 'high' ? 'error' : latestAssessment?.riskLevel === 'moderate' ? 'warning' : 'success'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {getTrendIcon(trend.trend)}
                              <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                {trend.trend}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {earliestAssessment?.totalScore || 0} → {latestAssessment?.totalScore || 0}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {/* Detailed Session Reports */}
        <Box className="section-with-heading">
          <Box className="heading-group">
            <Typography variant="h5" gutterBottom className="section-header" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 4 }}>
              <ScheduleIcon color="primary" />
              Detailed Session Reports
            </Typography>
          </Box>

          {reportData.sessions.map((session, sessionIndex) => (
            <Card key={session.sessionId} sx={{ mb: 3 }} className={sessionIndex > 0 ? 'print-break' : 'print-subsection'}>
              <CardContent>
                {/* Session Header */}
                <Box className="heading-group" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhotoLibraryIcon color="primary" />
                    Session {sessionIndex + 1}: {new Date(session.sessionDate).toLocaleDateString()}
                  </Typography>
                  <Chip 
                    label={session.sessionType === 'baseline' ? 'Baseline' : 'Follow-up'} 
                    color={session.sessionType === 'baseline' ? 'primary' : 'secondary'}
                    variant="filled"
                  />
                </Box>

                {/* Session Notes */}
                {session.notes && (
                  <Box className="summary-box" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Session Notes:</strong> {session.notes}
                    </Typography>
                  </Box>
                )}

                {/* Session Comparison */}
                {session.comparison && (
                  <Box className="summary-box" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Comparison with Baseline
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2">
                          <strong>Total:</strong> {session.comparison.totalMoles}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2" color="success.main">
                          <strong>New:</strong> {session.comparison.newMoles}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2" color="warning.main">
                          <strong>Changed:</strong> {session.comparison.changedMoles}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2">
                          <strong>Stable:</strong> {session.comparison.stableMoles}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {/* Macro Images and Annotations */}
                {session.macroImages.map((macroImage, imageIndex) => (
                  <Box key={macroImage.imageId} className="print-keep-together" sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" className="subsection-header" gutterBottom>
                      Image {imageIndex + 1} - {macroImage.annotations.length} Lesion(s)
                    </Typography>

                    {/* Annotations Table */}
                    {macroImage.annotations.length > 0 && (
                      <TableContainer sx={{ mb: 2 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell><strong>Lesion</strong></TableCell>
                              <TableCell><strong>Position</strong></TableCell>
                              <TableCell><strong>ABCDE</strong></TableCell>
                              <TableCell><strong>Risk</strong></TableCell>
                              <TableCell><strong>Last Assessment</strong></TableCell>
                              <TableCell><strong>Images</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {macroImage.annotations.map((annotation, annotationIndex) => {
                              const simplifiedLabel = simplifyMoleLabel(annotation.label, annotationIndex);
                              
                              return (
                                <TableRow key={annotation.annotationId}>
                                  <TableCell>{simplifiedLabel}</TableCell>
                                  <TableCell>
                                    {Math.round(annotation.position.x * 100)}%, {Math.round(annotation.position.y * 100)}%
                                  </TableCell>
                                  <TableCell>
                                    {annotation.abcdeAssessments.length > 0 ? (
                                      annotation.abcdeAssessments.map((assessment, idx) => (
                                        <Box key={assessment.assessmentId} sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mb: 0.5 }}>
                                          <Typography variant="caption">
                                            {new Date(assessment.assessmentDate).toLocaleDateString()}:
                                          </Typography>
                                          <Chip 
                                            label={`${assessment.scores.total}/7`} 
                                            size="small"
                                            color={assessment.riskLevel === 'high' ? 'error' : assessment.riskLevel === 'moderate' ? 'warning' : 'success'}
                                          />
                                        </Box>
                                      ))
                                    ) : (
                                      <Typography variant="body2" color="text.secondary">
                                        None
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={annotation.riskLevel} 
                                      color={annotation.riskLevel === 'high' ? 'error' : annotation.riskLevel === 'moderate' ? 'warning' : 'success'}
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {annotation.lastAssessmentDate 
                                      ? new Date(annotation.lastAssessmentDate).toLocaleDateString()
                                      : 'Never'
                                    }
                                  </TableCell>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                      <Chip label="M" size="small" variant="outlined" />
                                      {annotation.linkedMicroUrl && (
                                        <Chip label="µ" size="small" variant="outlined" />
                                      )}
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}

                    {/* High Risk Lesion Details */}
                    {macroImage.annotations
                      .filter(ann => ann.riskLevel === 'high' && ann.abcdeAssessments.length > 0)
                      .map((annotation, annotationIndex) => {
                        const simplifiedLabel = simplifyMoleLabel(annotation.label, annotationIndex);
                        
                        return (
                          <Box key={`detailed-${annotation.annotationId}`} className="print-keep-together" sx={{ 
                            p: 1, 
                            mb: 1, 
                            bgcolor: alpha(theme.palette.error.main, 0.05), 
                            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                            borderRadius: 1
                          }}>
                            <Typography variant="subtitle2" gutterBottom color="error.main">
                              ⚠ High Risk: {simplifiedLabel}
                            </Typography>
                            
                            {annotation.abcdeAssessments.slice(-1).map((assessment) => (
                              <Box key={assessment.assessmentId}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Latest: {new Date(assessment.assessmentDate).toLocaleDateString()}
                                </Typography>
                                
                                <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                  <Chip label={`A:${assessment.scores.asymmetry}`} size="small" />
                                  <Chip label={`B:${assessment.scores.borders}`} size="small" />
                                  <Chip label={`C:${assessment.scores.color}`} size="small" />
                                  <Chip label={`D:${assessment.scores.diameter}`} size="small" />
                                  <Chip label={`E:${assessment.scores.evolution}`} size="small" />
                                  <Chip label={`Total:${assessment.scores.total}/7`} color="error" size="small" />
                                </Box>
                                
                                {assessment.recommendation && (
                                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', fontSize: '0.8rem' }}>
                                    <strong>Action:</strong> {assessment.recommendation}
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Box>
                        );
                      })}
                  </Box>
                ))}
              </CardContent>
            </Card>
          ))}

          {/* Complete Mole Gallery Section */}
          <Card sx={{ mb: 3, mt: 4 }} className="print-break">
            <CardContent>
              <Box className="heading-group">
                <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhotoLibraryIcon color="primary" />
                  Complete Mole Gallery
                  <Chip 
                    label={`${reportData.summary.totalMoles} Total Lesions`} 
                    color="primary" 
                    variant="outlined"
                    size="small"
                  />
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Gallery of all monitored lesions across all sessions, organized chronologically.
                </Typography>
              </Box>

              {/* Mole Gallery - Fixed Layout for 4 columns */}
              <Box className="mole-gallery-container">
                {(() => {
                  // Flatten all annotations into a single array
                  const allAnnotations = reportData.sessions.flatMap((session) => 
                    session.macroImages.flatMap((macroImage) => 
                      macroImage.annotations.map((annotation) => ({
                        session,
                        macroImage,
                        annotation
                      }))
                    )
                  );

                  // Group annotations into rows of 4
                  const rows = [];
                  for (let i = 0; i < allAnnotations.length; i += 4) {
                    rows.push(allAnnotations.slice(i, i + 4));
                  }

                  return rows.map((row, rowIndex) => (
                    <Box key={`row-${rowIndex}`} className="mole-gallery-row print-keep-together" sx={{ display: 'flex', mb: 1, gap:1 }}>
                      {row.map(({ session, macroImage, annotation }, cellIndex) => {
                        const globalIndex = rowIndex * 4 + cellIndex;
                        const simplifiedLabel = simplifyMoleLabel(annotation.label, globalIndex);
                        
                        return (
                          <Box 
                            key={`${session.sessionId}_${macroImage.imageId}_${annotation.annotationId}`}
                            className="mole-gallery-cell"
                            sx={{ flex: '1 1 25%', maxWidth: '25%' }}
                          >
                            <Box className="mole-card" sx={{ 
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: 1,
                              p: 0.5,
                              bgcolor: 'background.paper',
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column'
                            }}>
                              {/* Mole Image */}
                              <Box sx={{ position: 'relative', mb: 0.5 }}>
                                <img
                                  className="mole-image"
                                  src={`${process.env.REACT_APP_API_URL}${annotation.linkedMicroUrl || macroImage.imageUrl}`}
                                  alt={`${annotation.label}`}
                                  style={{
                                    width: '100%',
                                    height: 120,
                                    objectFit: 'cover',
                                    borderRadius: 2
                                  }}
                                  onError={(e) => {
                                    if (annotation.linkedMicroUrl) {
                                      e.currentTarget.src = `${process.env.REACT_APP_API_URL}${macroImage.imageUrl}`;
                                    }
                                  }}
                                />
                                
                                {/* Risk Level Indicator */}
                                {annotation.riskLevel && (
                                  <Chip
                                    label={annotation.riskLevel.charAt(0).toUpperCase()}
                                    size="small"
                                    color={annotation.riskLevel === 'high' ? 'error' : annotation.riskLevel === 'moderate' ? 'warning' : 'success'}
                                    sx={{
                                      position: 'absolute',
                                      top: 1,
                                      right: 1,
                                      fontSize: '0.5rem',
                                      height: 12,
                                      minWidth: 12
                                    }}
                                  />
                                )}
                              </Box>

                              {/* Compact Mole Information */}
                              <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.6rem', mb: 0.25, display: 'block' }}>
                                  {simplifiedLabel}
                                </Typography>
                                
                                {/* <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.5rem', mb: 0.25, display: 'block' }}>
                                  {new Date(session.sessionDate).toLocaleDateString()}
                                </Typography> */}

                                {/* ABCDE Score - Very Compact */}
                                {annotation.abcdeAssessments.length > 0 && (
                                  <Box sx={{ display: 'flex', gap: 0.125, mb: 0.25, flexWrap: 'wrap' }}>
                                    {(() => {
                                      const latest = annotation.abcdeAssessments[annotation.abcdeAssessments.length - 1];
                                      return (
                                        <>
                                          <Typography variant="caption" sx={{ fontSize: '0.45rem' }}>
                                            A{latest.scores.asymmetry}
                                          </Typography>
                                          <Typography variant="caption" sx={{ fontSize: '0.45rem' }}>
                                            B{latest.scores.borders}
                                          </Typography>
                                          <Typography variant="caption" sx={{ fontSize: '0.45rem' }}>
                                            C{latest.scores.color}
                                          </Typography>
                                          <Typography variant="caption" sx={{ fontSize: '0.45rem' }}>
                                            D{latest.scores.diameter}
                                          </Typography>
                                          <Typography variant="caption" sx={{ fontSize: '0.45rem' }}>
                                            E{latest.scores.evolution}
                                          </Typography>
                                          <Typography variant="caption" sx={{ fontSize: '0.45rem', fontWeight: 600 }}>
                                            ={latest.scores.total}
                                          </Typography>
                                        </>
                                      );
                                    })()}
                                  </Box>
                                )}

                                {/* Image Types - Minimal */}
                                <Box sx={{ display: 'flex', gap: 0.125, mt: 'auto' }}>
                                  <Typography variant="caption" sx={{ fontSize: '0.4rem', color: 'text.secondary' }}>
                                    {annotation.linkedMicroUrl ? 'µ' : 'M'}
                                  </Typography>
                                  {annotation.segmentation && (
                                    <Typography variant="caption" sx={{ fontSize: '0.4rem', color: 'secondary.main' }}>
                                      A
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        );
                      })}
                      
                      {/* Fill empty cells */}
                      {row.length < 4 && Array.from({ length: 4 - row.length }).map((_, emptyIndex) => (
                        <Box 
                          key={`empty-${rowIndex}-${emptyIndex}`}
                          className="mole-gallery-cell"
                          sx={{ flex: '1 1 25%', maxWidth: '25%' }}
                        />
                      ))}
                    </Box>
                  ));
                })()}
              </Box>

              {/* Gallery Summary */}
              <Box sx={{ mt: 2, p: 1, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Summary:</strong> {reportData.summary.totalMoles} lesions, {reportData.summary.totalABCDEAssessments} assessments.
                  {reportData.summary.highRiskMoles > 0 && (
                    <span style={{ color: theme.palette.error.main, fontWeight: 600 }}>
                      {' '}⚠ {reportData.summary.highRiskMoles} high-risk.
                    </span>
                  )}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Professional Footer */}
        <Paper elevation={1} className="report-footer" sx={{ p: 2, mt: 4, bgcolor: alpha(theme.palette.grey[500], 0.05) }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <img src="/assets/noetivblack.png" alt="Noetiv Logo" style={{ width: 60, height: 'auto' }} />
              </Box>
            </Grid>
            
            <Grid item xs={12} md={8}>
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                Generated from Mole.Noetiv system. Consult dermatologist for clinical decisions.
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', display: 'block' }}>
                {new Date(reportData.reportDate).toLocaleDateString()}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', display: 'block' }}>
                ID: {reportData.patientId}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Add Snackbar for PDF generation feedback */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
        />
      </Box>
    </>
  );
};

export default PatientReport;
